<template>
  <div class="quote-groups-container">
    <div v-if="quotes.length > 0" class="quotes-grid">
      <div v-for="(quote, i) in quotes" :key="i" class="quote-card-container">
        <v-card @click="go(quote)" class="quote-card" elevation="2" hover>
          <!-- Header Section -->
          <div class="quote-header" :class="{
            'rank-1-header': getBestPriceRank(quote) === 1,
            'rank-2-header': getBestPriceRank(quote) === 2,
            'rank-3-header': getBestPriceRank(quote) === 3
          }">
            <div class="quote-status-row">
              <!-- Mostrar solo en cotización seleccionada o cuando no hay orden -->
              <v-chip v-if="quote[0].supplier_id === quote[0].order_supplier_id || !quote[0].order_id"
                :color="quote[0].supplier_id === quote[0].order_supplier_id ? 'success' : 'warning'" dark small label
                class="status-chip">
                <v-icon left x-small>
                  {{ quote[0].supplier_id === quote[0].order_supplier_id ? 'mdi-check-circle' : 'mdi-clock' }}
                </v-icon>
                {{ quote[0].supplier_id === quote[0].order_supplier_id ? 'Convertida' : 'Pendiente' }}
              </v-chip>
            </div>

            <div class="quote-title-section mt-n2">
              <h2 class="quote-number">
                COTIZACIÓN #{{ quote[0].quotation_id }}
              </h2>
              <p class="business-name mt-n3">{{ quote[0].bussines_unit_description }}</p>


              <!-- Best Price Badges (Inside header) -->
              <div class="price-indicators">
                <!-- Individual Items Best Price Badge -->
                <v-tooltip bottom v-if="getBestPriceCount(quote) > 0">
                  <template v-slot:activator="{ on, attrs }">
                    <div :class="[
                      'best-price-banner',
                      { 'best-price-banner-low': getBestPriceRank(quote) === 3 },
                      { 'best-price-banner-medium': getBestPriceRank(quote) === 2 },
                      { 'best-price-banner-high': getBestPriceRank(quote) === 1 }
                    ]" v-bind="attrs" v-on="on">
                      <v-icon left small
                        :class="[getBestPriceRank(quote) <= 2 ? 'trophy-icon-animated' : 'trophy-icon']">
                        {{ getBestPriceRank(quote) === 1 ? 'mdi-trophy' : (getBestPriceRank(quote) === 2 ?
                          'mdi-trophy-award' : 'mdi-trophy-outline') }}
                      </v-icon>
                      <span>
                        <strong>
                          {{ getBestPriceRank(quote) === 1 ? 'Mayor' : (getBestPriceRank(quote) === 2 ? 'Buen' :
                            'Mejor') }}
                        </strong>
                        {{ getBestPriceRank(quote) === 1 ? 'cantidad' : 'precio' }}
                        <v-chip x-small class="ml-1 white--text"
                          :color="getBestPriceRank(quote) === 1 ? 'amber accent-4' : (getBestPriceRank(quote) === 2 ? 'amber darken-1' : 'amber darken-2')">
                          {{ getBestPriceCount(quote) }}/{{ quote.length }}
                        </v-chip>
                        <v-progress-linear :value="getBestPricePercentage(quote)" height="3" rounded
                          :color="getBestPriceRank(quote) === 3 ? 'amber darken-2' : (getBestPriceRank(quote) === 2 ? 'amber darken-1' : 'amber accent-4')"
                          class="mt-1"></v-progress-linear>
                      </span>
                    </div>
                  </template>
                  <span>
                    <strong>{{ getBestPriceCount(quote) }}</strong> de {{ quote.length }} artículos ({{
                      getBestPricePercentage(quote) }}%)
                    tienen el mejor precio individual.
                    <br>Ranking de cotización: <strong>#{{ getBestPriceRank(quote) }}</strong>
                  </span>
                </v-tooltip>

                <!-- Best Total Price Badge -->
                <v-tooltip bottom v-if="isLowestTotalPrice(quote)">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="best-total-banner" v-bind="attrs" v-on="on">
                      <v-icon left small class="medal-icon">mdi-medal</v-icon>
                      <span>Mejor Precio Total</span>
                    </div>
                  </template>
                  <span>Este proveedor ofrece el precio total más bajo: {{ currencyFormat(total(quote)) }}</span>
                </v-tooltip>

                <!-- Savings Badge -->
                <v-tooltip bottom v-if="getSavingsAmount(quote) > 0">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="savings-banner" v-bind="attrs" v-on="on">
                      <v-icon left small class="savings-icon">mdi-currency-usd-off</v-icon>
                      <span>Ahorro: {{ currencyFormat(getSavingsAmount(quote)) }}</span>
                    </div>
                  </template>
                  <span>Comparado con el precio más alto ({{ currencyFormat(getHighestTotal()) }})</span>
                </v-tooltip>
              </div>
            </div>
          </div>

          <!-- Info Section -->
          <div class="quote-info">
            <div class="info-grid">
              <div v-if="quote[0].supplier_id === quote[0].order_supplier_id" class="info-item">
                <span class="info-label">Orden:</span>
                <span class="info-value order-link">
                  #{{ filLeft(quote[0].order_id?.toString() || '', 12, "0") }}
                </span>
              </div>

              <div class="info-item">
                <span class="info-label">Proveedor:</span>
                <span class="info-value supplier-name">
                  {{ quote[0].supplier_name }}
                </span>
              </div>

              <div class="info-item">
                <span class="info-label">Creación:</span>
                <span class="info-value">
                  {{ formatDate(quote[0].created_at) }}
                </span>
              </div>

              <div class="info-item">
                <span class="info-label">Solicitante:</span>
                <span class="info-value">
                  {{ quote[0].use_fullname }}
                </span>
              </div>
            </div>
          </div>

          <!-- Items Preview -->
          <div class="items-preview">
            <div class="items-header">
              <h4 class="items-title">
                <v-icon small color="primary">mdi-package-variant</v-icon>
                Artículos ({{ quote.length }})
              </h4>
              <div class="total-preview">
                <span class="total-amount">{{ currencyFormat(total(quote)) }}</span>
              </div>
            </div>

            <div class="items-list">
              <div v-for="(item, index) in quote.slice(0, 3)" :key="index" class="item-row"
                :class="{ 'best-price-item': item.isBestPrice }">
                <div class="item-code">{{ item.quotation_detail_quantity }}</div>
                <div class="item-name" :title="item.item_name">{{ item.item_name }}</div>
                <div class="item-details">
                  <div class="price" :class="{ 'best-price-value': item.isBestPrice }">
                    {{ currencyFormat(item.price || 0) }}
                    <v-tooltip bottom v-if="item.isBestPrice">
                      <template v-slot:activator="{ on, attrs }">
                        <span class="best-price" v-bind="attrs" v-on="on">
                          <v-icon x-small class="trophy-icon-small">mdi-trophy</v-icon>
                          <span>Mejor</span>
                        </span>
                      </template>
                      <span>Este es el mejor precio para este artículo</span>
                    </v-tooltip>
                  </div>
                </div>
              </div>

              <div v-if="quote.length > 3" class="more-items">
                <span class="more-text">
                  + {{ quote.length - 3 }} artículo{{ quote.length - 3 > 1 ? 's' : '' }} más
                </span>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="quote-footer">
            <v-btn color="primary" outlined small block class="view-btn">
              <v-icon left small>mdi-eye</v-icon>
              Ver Detalles
            </v-btn>
          </div>
        </v-card>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-content">
        <v-icon size="64" color="grey lighten-1">mdi-file-document-outline</v-icon>
        <h3 class="empty-title">No hay cotizaciones disponibles</h3>
        <p class="empty-text">Las cotizaciones aparecerán aquí cuando estén disponibles</p>
      </div>
    </div>
  </div>
</template>

<script>
import QuoteDetailsVue from "./QuoteDetails.vue";

export default {
  components: { QuoteDetailsVue },

  data() {
    return {
      quotes: [],
      bestPrices: {},
      options: {},
      modalQuote: [],
      loading: false,
      showDialog: false,
      totalPriceData: {
        lowestTotal: null,
        highestTotal: null,
        lowestSupplier: null
      }
    };
  },

  props: {
    quote: {
      type: String,
      required: true
    },
  },

  created() {
    this.init();
  },

  watch: {
    quote(val, oldVal) {
      if (val !== oldVal) {
        this.init();
      }
    },
  },

  methods: {
    isBestPrice(itemId, supplierId, price) {
      if (!this.bestPrices[itemId] || price <= 0) return false;
      return this.bestPrices[itemId].suppliers.includes(supplierId);
    },

    getBestPriceCount(quote) {
      return quote.filter(item => item.isBestPrice).length;
    },

    getBestPricePercentage(quote) {
      const bestCount = this.getBestPriceCount(quote);
      return quote.length > 0 ? Math.round((bestCount / quote.length) * 100) : 0;
    },

    getBestPriceRank(quote) {
      if (!this.quotes || this.quotes.length === 0) return 3;

      // Create array of quotes with their best price counts and total prices
      const quotesWithMetrics = this.quotes.map(q => ({
        quote: q,
        bestCount: this.getBestPriceCount(q),
        totalPrice: this.total(q),
        isLowestTotal: this.isLowestTotalPrice(q)
      }));

      // Sort by best count (descending) and then by total price (ascending)
      quotesWithMetrics.sort((a, b) => {
        if (a.bestCount !== b.bestCount) {
          return b.bestCount - a.bestCount; // Highest count first
        }
        return a.totalPrice - b.totalPrice; // Lowest price first if count is tied
      });

      // Find position of current quote
      const index = quotesWithMetrics.findIndex(item => item.quote === quote);

      // If it has the lowest total price, it's rank 1 regardless of best count
      if (this.isLowestTotalPrice(quote)) return 1;

      // If it has the most best prices, it's also rank 1
      if (index === 0) return 1;

      // If it's the last one, it's rank 3
      if (index === this.quotes.length - 1) return 3;

      // Everything else is rank 2
      return 2;
    },

    getBestPriceText(quote) {
      const bestCount = this.getBestPriceCount(quote);
      const total = quote.length;
      return `${bestCount} de ${total} artículos tienen el mejor precio`;
    },

    // Determina si este proveedor tiene el precio total más bajo
    isLowestTotalPrice(quote) {
      if (!this.totalPriceData.lowestSupplier) return false;
      return quote[0].supplier_id === this.totalPriceData.lowestSupplier;
    },

    // Obtiene el monto de ahorro comparado con el precio total más alto
    getSavingsAmount(quote) {
      if (!this.totalPriceData.highestTotal) return 0;
      const currentTotal = this.total(quote);
      return this.totalPriceData.highestTotal - currentTotal;
    },

    // Obtiene el precio total más alto entre todas las cotizaciones
    getHighestTotal() {
      return this.totalPriceData.highestTotal || 0;
    },

    // Verifica si la cotización tiene order_id (convertida a orden)
    hasOrderId(quote) {
      return quote && quote.length > 0 && quote[0].order_id;
    },


    go(item) {
      this.$router.push({
        name: "QuoteDetailView",
        params: {
          quotation_id: item[0].quotation_id_number,
          supplier_id: item[0].supplier_id,
        },
      });
    },

    total(items) {
      return items.reduce((total, item) => {
        const price = item.price || 0;
        const quantity = item.quotation_detail_quantity || 0;
        return total + (price * quantity);
      }, 0);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },

    async init() {
      try {
        const request = await this.api_Get("shopping/quotesDetails/" + this.quote);
        const data = await request.json();
        const groupedQuotes = this.groupBy(data, "supplier_id");
        const result = [];
        const priceMap = {};

        // First pass: Collect all prices for each item
        groupedQuotes.forEach(group => {
          group.forEach(item => {
            if (!priceMap[item.item_id]) {
              priceMap[item.item_id] = [];
            }
            priceMap[item.item_id].push({
              price: parseFloat(item.price || 0),
              supplier_id: item.supplier_id
            });
          });
        });

        // Find best price for each item
        this.bestPrices = {};
        Object.keys(priceMap).forEach(itemId => {
          const prices = priceMap[itemId];
          if (prices.length > 0) {
            // Find minimum non-zero price
            const validPrices = prices.filter(p => p.price > 0);
            if (validPrices.length > 0) {
              const minPrice = Math.min(...validPrices.map(p => p.price));
              this.bestPrices[itemId] = {
                price: minPrice,
                suppliers: prices
                  .filter(p => Math.abs(p.price - minPrice) < 0.01 && p.price > 0)
                  .map(p => p.supplier_id)
              };
            }
          }
        });

        // Process quotes data
        for (let group of groupedQuotes) {
          const processedData = [];

          group.reduce((accumulator, currentValue) => {
            if (!accumulator[currentValue.item_id]) {
              accumulator[currentValue.item_id] = { ...currentValue };
              // Add isBestPrice flag
              accumulator[currentValue.item_id].isBestPrice = this.isBestPrice(
                currentValue.item_id,
                currentValue.supplier_id,
                parseFloat(currentValue.price || 0)
              );
              processedData.push(accumulator[currentValue.item_id]);
            }

            accumulator[currentValue.item_id].quantity += parseFloat(currentValue.quantity || 0);
            return accumulator;
          }, {});

          result.push(processedData);
        }

        // Calculate total prices for all quotes to find lowest and highest
        let lowestTotal = Number.MAX_SAFE_INTEGER;
        let highestTotal = 0;
        let lowestSupplier = null;

        result.forEach(quote => {
          const totalPrice = this.total(quote);
          if (totalPrice < lowestTotal && totalPrice > 0) {
            lowestTotal = totalPrice;
            lowestSupplier = quote[0].supplier_id;
          }
          if (totalPrice > highestTotal) {
            highestTotal = totalPrice;
          }
        });

        // Store total price data
        this.totalPriceData = {
          lowestTotal,
          highestTotal,
          lowestSupplier
        };

        // Sort quotes by: 1. Has best total price, 2. Number of best price items
        result.sort((a, b) => {
          // First sort by best total price
          const aTotalPrice = this.total(a);
          const bTotalPrice = this.total(b);

          if (aTotalPrice === lowestTotal && bTotalPrice !== lowestTotal) return -1;
          if (bTotalPrice === lowestTotal && aTotalPrice !== lowestTotal) return 1;

          // Then sort by best price count
          const aBestCount = a.filter(item => item.isBestPrice).length;
          const bBestCount = b.filter(item => item.isBestPrice).length;
          return bBestCount - aBestCount;
        });

        this.quotes = result;
      } catch (error) {
        console.error('Error loading quotes:', error);
        this.quotes = [];
      }
    },

    groupBy(collection, property) {
      const values = [];
      const result = [];

      for (let i = 0; i < collection.length; i++) {
        const val = collection[i][property];
        const index = values.indexOf(val);

        if (index > -1) {
          result[index].push(collection[i]);
        } else {
          values.push(val);
          result.push([collection[i]]);
        }
      }

      return result;
    },
  },
};
</script>

<style scoped>
.quote-groups-container {
  padding: 16px;

  min-height: 100vh;
}

/* Grid Layout */
.quotes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.quote-card-container {
  height: fit-content;
}

/* Quote Card */
.quote-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
}

.quote-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 15px rgba(33, 150, 243, 0.15) !important;
}

/* Header Section */
.quote-header {
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  padding: 16px 20px;
  position: relative;
  height: auto;
  min-height: 140px;
}

/* Encabezados según ranking */
.rank-1-header {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
  border-bottom: 4px solid #ff9800;
  box-shadow: inset 0 -2px 10px rgba(255, 152, 0, 0.3);
}

.rank-2-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  border-bottom: 2px solid #ffb74d;
}

.rank-3-header {
  background: linear-gradient(135deg, #42a5f5 0%, #1976d2 100%);
  border-bottom: 1px solid #e0e0e0;
}

.quote-status-row {
  display: flex;
  width: 100%;
  margin-bottom: 8px;
  justify-content: flex-start;
}

.price-indicators {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.best-price-banner {
  display: flex;
  align-items: center;
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
  width: fit-content;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

/* Variante para 1 solo mejor precio */
.best-price-banner-low {
  background: linear-gradient(135deg, #ffc107 0%, #ffb300 100%);
  box-shadow: 0 2px 4px rgba(255, 143, 0, 0.2);
  border: 1px solid rgba(255, 193, 7, 0.3);
}

/* Variante para 2 mejores precios */
.best-price-banner-medium {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  box-shadow: 0 2px 6px rgba(255, 143, 0, 0.3);
  animation: pulse 3s infinite;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

/* Variante para 3 o más mejores precios */
.best-price-banner-high {
  background: linear-gradient(135deg, #ff6f00 0%, #e65100 100%);
  box-shadow: 0 3px 8px rgba(255, 111, 0, 0.4);
  animation: pulse 2s infinite;
  border: 1px solid rgba(230, 81, 0, 0.3);
}

.best-total-banner {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
  width: fit-content;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
}

.savings-banner {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #3f51b5 0%, #1a237e 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 2px 6px rgba(63, 81, 181, 0.3);
  width: fit-content;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
}

.best-price-banner::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shine 2s infinite;
  transform: translateX(-100%);
}

.quote-title-section {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
}

.quote-number {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 6px 0;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.business-name {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 3px 0;
  opacity: 0.95;
}

.location-name {
  font-size: 0.8rem;
  font-weight: 400;
  margin: 0;
  opacity: 0.85;
}

/* Info Section */
.quote-info {
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #424242;
  min-width: 80px;
  flex-shrink: 0;
}

.info-value {
  font-size: 0.85rem;
  font-weight: 500;
  color: #616161;
  word-break: break-word;
}

.order-link {
  color: #2196f3;
  text-decoration: underline;
  font-weight: 600;
}

.supplier-name {
  color: #4caf50;
  font-weight: 600;
}

/* Items Preview */
.items-preview {
  padding: 16px 20px;
  flex: 1;
}

.items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.items-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #424242;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.total-preview {
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 1px 3px rgba(76, 175, 80, 0.3);
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.item-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.item-row:hover {
  background: #eeeeee;
  border-color: #bdbdbd;
}

.best-price-item {
  border: 1px solid #ff8f00;
  border-left: 4px solid #ff8f00;
  background: #fff8e1;
  position: relative;
}

.best-price-item:hover {
  background: #ffecb3;
  border-color: #ff6f00;
}

.best-price-item::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 12px 12px 0;
  border-color: transparent #ff8f00 transparent transparent;
}

.item-code {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #424242;
  background: white;
  padding: 2px 5px;
  border-radius: 2px;
  font-size: 0.75rem;
  border: 1px solid #bdbdbd;
}

.item-name {
  font-weight: 500;
  color: #424242;
  font-size: 0.8rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.quantity {
  font-size: 0.75rem;
  color: #616161;
  font-weight: 500;
}

.price {
  font-size: 0.85rem;
  color: #4caf50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

.best-price-value {
  color: #ff6f00;
  font-weight: 700;
  font-size: 0.9rem;
}

.best-price {
  background: linear-gradient(135deg, #ff8f00 0%, #ff6f00 100%);
  color: white !important;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.65rem;
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(255, 111, 0, 0.3);
  transition: all 0.2s ease;
}

.best-price:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(255, 111, 0, 0.4);
}

.best-price-chip {
  background: linear-gradient(135deg, #f57f17 0%, #ff8f00 100%);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  animation: pulse 2s infinite;
  margin-left: auto;
  font-weight: 700;
}

.trophy-icon {
  color: #fff4de;
  margin-right: 8px;
  transform: scale(1.1);
  transition: all 0.3s ease;
}

.trophy-icon-animated {
  color: #fffae0;
  margin-right: 8px;
  animation: trophyPulse 2s infinite alternate;
  transform: scale(1.2);
  filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.6));
}

@keyframes trophyPulse {
  0% {
    transform: scale(1.2) rotate(-5deg);
    filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.6));
  }

  50% {
    transform: scale(1.3) rotate(0deg);
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.8));
  }

  100% {
    transform: scale(1.2) rotate(5deg);
    filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.6));
  }
}

.medal-icon {
  color: #e8f5e9;
  margin-right: 6px;
  animation: bounce 2s infinite alternate;
  transform: scale(1.2);
}

.savings-icon {
  color: #e8eaf6;
  margin-right: 6px;
  animation: flash 3s infinite;
  transform: scale(1.2);
}

.trophy-icon-small {
  color: #fff4de;
  transform: scale(1.2);
  margin-right: 3px;
}

.more-items {
  text-align: center;
  padding: 6px;
  color: #616161;
  font-style: italic;
  font-size: 0.8rem;
}

.more-text {
  background: #e0e0e0;
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
}

/* Footer */
.quote-footer {
  padding: 12px 20px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

.view-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 30px;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  height: 36px;
  letter-spacing: 0.5px;
}

.view-btn:hover {
  background: #2196f3;
  color: white;
  border-color: #2196f3;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px;
}

.empty-content {
  text-align: center;
  max-width: 400px;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #64748b;
  margin: 20px 0 8px 0;
}

.empty-text {
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
}

/* Animations */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 143, 0, 0.4);
  }

  70% {
    box-shadow: 0 0 0 6px rgba(255, 143, 0, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(255, 143, 0, 0);
  }
}

@keyframes rotate {
  0% {
    transform: rotate(-10deg) scale(1.2);
  }

  100% {
    transform: rotate(10deg) scale(1.2);
  }
}

@keyframes shine {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

@keyframes bounce {
  0% {
    transform: scale(1.2);
  }

  50% {
    transform: scale(1.4);
  }

  100% {
    transform: scale(1.2);
  }
}

@keyframes flash {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }

  100% {
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .quote-groups-container {
    padding: 12px;
  }

  .quotes-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .quote-header {
    padding: 12px 16px;
    height: auto;
  }

  .quote-number {
    font-size: 1.1rem;
  }

  .info-grid {
    gap: 6px;
  }

  .items-header {
    flex-direction: column;
    align-items: stretch;
  }

  .total-preview {
    text-align: center;
  }

  .item-row {
    grid-template-columns: 1fr;
    gap: 6px;
    text-align: left;
  }

  .item-details {
    align-items: flex-start;
    flex-direction: row;
    gap: 6px;
  }
}

@media (max-width: 480px) {
  .quotes-grid {
    grid-template-columns: 1fr;
  }

  .quote-header {
    padding: 16px 20px;
  }

  .quote-info,
  .items-preview,
  .quote-footer {
    padding: 16px 20px;
  }
}

/* Dark Theme Support */
.theme--dark .quote-card {
  background: #1e293b;
  border-color: #475569;
}

.theme--dark .quote-info {
  background: #334155;
  border-color: #475569;
}

.theme--dark .items-preview {
  background: #1e293b;
}

.theme--dark .quote-footer {
  background: #334155;
  border-color: #475569;
}

.theme--dark .item-row {
  background: #334155;
  border-color: #475569;
}

.theme--dark .item-row:hover {
  background: #475569;
  border-color: #64748b;
}

.theme--dark .item-code {
  background: #475569;
  border-color: #64748b;
  color: #cbd5e1;
}

.theme--dark .more-text {
  background: #475569;
  color: #cbd5e1;
}
</style>